"use client";

/**
 * ReelsFeed - Instagram/TikTok-style vertical reels feed
 *
 * Optimized for performance:
 * - Single video at a time (no overlapping audio/video)
 * - Cached API calls (see muxApi.ts)
 * - Minimal re-renders
 * - Native-feeling swipe gestures
 *
 * A/B (PostHog multivariate flag `reels_feed_ab_style`):
 * - `classic` — full list from API + existing end screen (control)
 * - `three_teaser` — first N reels only (default 3); on the last teaser reel a **solid**
 *   “see more” CTA appears (no backdrop-blur on the video). Create the flag in PostHog
 *   with variants `classic` / `three_teaser` (rollout % as needed).
 */

import { getReelsMuxPlaybackIds } from "@/lib/muxApi";
import {
  getFeatureFlagPostHog,
  trackFeatureFlagPostHog,
} from "@/lib/posthogAnalytics";
import {
  DEFAULT_REELS_AB_WHEN_FLAG_MISSING,
  POSTHOG_FLAG_REELS_FEED_AB_STYLE,
  normalizeReelsFeedAbFlagValue,
} from "@/lib/premiumInvitePosthogFlags";
import { trackComunidadReelsVipClickPostHog } from "@/lib/comunidadPostHog";
import {
  trackMuxCheckoutClick,
  trackMuxCtaViewed,
} from "@/lib/muxPaywallFunnelTracking";
import { BRAND_MERCADOPAGO_HANDSHAKE_URL } from "@/lib/brandConfig";
import { isArgentinaPaymentContext } from "@/lib/resolveCheckoutCountryCode";
import MuxPlayer from "@mux/mux-player-react";
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Heart,
  Play,
  RefreshCw,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Mux Data env key — MUST be static `import.meta.env.VITE_MUX_DATA_ENV_KEY`
 * so Vite (and vite.config define) inlines it at build time. Dynamic access
 * like import.meta.env[name] is NOT replaced and ships empty in prod bundles.
 * Railway Docker must pass ARG/ENV VITE_MUX_DATA_ENV_KEY into the build stage.
 */
const MUX_DATA_ENV_KEY =
  (import.meta.env.VITE_MUX_DATA_ENV_KEY as string | undefined)?.trim() ||
  undefined;

/** Compat: mismo string que `POSTHOG_FLAG_REELS_FEED_AB_STYLE`. */
export const REELS_FEED_AB_FLAG = POSTHOG_FLAG_REELS_FEED_AB_STYLE;

export type ReelsFeedAbVariant = "classic" | "three_teaser";

function readReelsAbFromPosthog(
  getRaw: () => unknown,
  fallback: ReelsFeedAbVariant,
): ReelsFeedAbVariant {
  const parsed = normalizeReelsFeedAbFlagValue(getRaw());
  return parsed ?? fallback;
}

function captureReelsAnalytics(
  event: string,
  props: Record<string, unknown>
): void {
  if (typeof window === "undefined") return;
  try {
    (
      window as Window & { posthog?: { capture?: (e: string, p?: object) => void } }
    ).posthog?.capture?.(event, { ...props, surface: "reels_feed" });
  } catch {
    /* no-op */
  }
}

// ── Mini i18n for ReelsFeed UI strings ──────────────────────────────────────
const REELS_T = {
  loading:        { en: "Loading...",                          es: "Cargando..." },
  noVideos:       { en: "No videos available",                 es: "No hay videos disponibles" },
  back:           { en: "← Back",                             es: "← Volver" },
  loadError:      { en: "Video unavailable",                   es: "No se pudo cargar" },
  retry:          { en: "Retry",                               es: "Reintentar" },
  next:           { en: "Next",                                es: "Siguiente" },
  watchAgain:     { en: "Watch again",                         es: "Ver de nuevo" },
  seeMoreContent: { en: "Want to see more exclusive content?", es: "¿Querés ver más contenido exclusivo?" },
  seeMore:        { en: "See more",                            es: "Ver más" },
  freeDefault:    { en: "Free Access",                         es: "Acceso Gratis" },
  vipDefault:     { en: "Exclusive Member",                    es: "Ser VIP / ver más" },
  muteOn:         { en: "Unmute",                              es: "Activar sonido" },
  muteOff:        { en: "Mute",                                es: "Silenciar" },
} as const;

// Hook to detect mobile devices
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
};

// Hook to detect iOS
const useIsIOS = () => {
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice =
      /iphone|ipad|ipod/.test(userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);
  }, []);

  return isIOS;
};

interface ReelsFeedProps {
  onVideoPlay?: (index: number, playbackId: string) => void;
  onVideoChange?: (index: number, playbackId: string) => void;
  fallbackPlaybackIds?: string[];
  onReachEnd?: () => void;
  /**
   * Si viene no vacío, se usa este orden fijo (IDs Mux) y no se llama a la API de listado.
   * Misma semántica que la lista curada del admin premium invite.
   */
  playbackIdsPreset?: string[];
  /** Skip PostHog and force layout (e.g. previews). */
  abVariantOverride?: ReelsFeedAbVariant;
  /**
   * Si PostHog no devuelve variante para `reels_feed_ab_style`, usar esto (alineado con default DB/admin).
   */
  abVariantFallback?: ReelsFeedAbVariant;
  /** Destination for “Ver más” in `three_teaser` (third reel). Default `/invitacion-premium`. */
  seeMoreHref?: string;
  /** Teaser count when variant is `three_teaser`. Default 3. */
  teaserReelCount?: number;
  /** Optional: canal Telegram público gratis — usado en pantalla final (embudo premium invite). */
  freeTelegramUrl?: string;
  /** VIP / “ver más” (ej. MP AR). Pantalla final sin blur sobre el video. */
  vipMoreHref?: string;
  /** Suscripción Stripe intl — sin href; el caller dispara checkout membresía VIP. */
  onVipCheckoutClick?: () => void;
  /** Copy para CTAs de la pantalla final cuando hay `freeTelegramUrl` y/o `vipMoreHref`. */
  endScreenCtaLabels?: {
    headline?: string;
    freeTelegram?: string;
    vip?: string;
  };
  /** BCP-47 language code from Worker (e.g. "es-AR", "en-US"). Drives UI string lang. Default "es". */
  language?: string;
  /**
   * Secret share token (32 hex). When set, sends play/heartbeat/ended beacons
   * to /api/reel-invitations/engagement and Mux Data metadata for watch time.
   */
  engagementToken?: string | null;
  /** Optional env key for Mux Data (client). Falls back to VITE_MUX_DATA_ENV_KEY. */
  muxDataEnvKey?: string | null;
}

function sendSecretShareBeacon(
  payload: {
    token: string;
    event: "play" | "heartbeat" | "ended" | "progress" | "error";
    playback_id?: string;
    position_seconds?: number;
    duration_seconds?: number;
    delta_watch_seconds?: number;
    error_message?: string;
    error_code?: string;
  },
  opts?: { preferBeacon?: boolean },
): void {
  try {
    const body = JSON.stringify(payload);
    // Prefer fetch+JSON: Express/Railway parse Content-Type reliably.
    // sendBeacon only as last resort on page hide (some parsers drop empty body).
    if (opts?.preferBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        "/api/reel-invitations/engagement",
        new Blob([body], { type: "application/json" }),
      );
      if (ok) return;
    }
    void fetch("/api/reel-invitations/engagement", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body,
      keepalive: true,
      cache: "no-store",
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

const ReelsFeed: React.FC<ReelsFeedProps> = ({
  onVideoPlay,
  onVideoChange,
  fallbackPlaybackIds = [],
  onReachEnd,
  playbackIdsPreset,
  abVariantOverride,
  abVariantFallback = DEFAULT_REELS_AB_WHEN_FLAG_MISSING,
  seeMoreHref = "/invitacion-premium",
  teaserReelCount = 3,
  freeTelegramUrl = "",
  vipMoreHref = "",
  onVipCheckoutClick,
  endScreenCtaLabels,
  language = "es",
  engagementToken = null,
  muxDataEnvKey = null,
}) => {
  const isEnglish = language.split("-")[0].toLowerCase() === "en";
  const rt = (key: keyof typeof REELS_T) => REELS_T[key][isEnglish ? "en" : "es"];
  // Core state
  const [playbackIds, setPlaybackIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isBuffering, setIsBuffering] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Touch/swipe state
  const [touchStart, setTouchStart] = useState<{
    y: number;
    time: number;
  } | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const hasReachedEndRef = useRef(false);
  const isMobile = useIsMobile();
  const isIOS = useIsIOS();
  /** Secret share watch-time accounting */
  const lastBeaconAtRef = useRef<number>(Date.now());
  const lastPositionRef = useRef<number>(0);
  const lastDurationRef = useRef<number>(0);
  const playSentForIndexRef = useRef<number | null>(null);
  const engagementTokenRef = useRef(engagementToken);
  engagementTokenRef.current = engagementToken;
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  const effectiveIdsRef = useRef<string[]>([]);

  const [abVariant, setAbVariant] = useState<ReelsFeedAbVariant>(() =>
    abVariantOverride ?? abVariantFallback
  );
  const lastAbExposureKeyRef = useRef<string | null>(null);
  const seeMoreImpressionForIndex = useRef<number | null>(null);

  // Resolve A/B variant from PostHog (multivariate) unless overridden.
  useEffect(() => {
    if (abVariantOverride) {
      setAbVariant(abVariantOverride);
      return;
    }

    const read = () =>
      readReelsAbFromPosthog(
        () => getFeatureFlagPostHog(REELS_FEED_AB_FLAG),
        abVariantFallback,
      );

    setAbVariant(read());

    const ph = (
      window as Window & { posthog?: { onFeatureFlags?: (cb: () => void) => void } }
    ).posthog;
    ph?.onFeatureFlags?.(() => {
      setAbVariant(read());
    });
  }, [abVariantOverride, abVariantFallback]);

  useEffect(() => {
    const exposureKey = abVariantOverride
      ? `override:${abVariantOverride}`
      : abVariant;
    if (lastAbExposureKeyRef.current === exposureKey) return;
    lastAbExposureKeyRef.current = exposureKey;
    if (!abVariantOverride) {
      trackFeatureFlagPostHog(REELS_FEED_AB_FLAG, abVariant);
    }
    captureReelsAnalytics("reels_feed_ab_exposure", {
      variant: abVariant,
      source: abVariantOverride ? "override" : "posthog",
    });
  }, [abVariant, abVariantOverride]);

  const threeTeaserActive = abVariant === "three_teaser";
  const teaserCap = Math.max(1, Math.min(20, teaserReelCount));

  const trimmedFreeTelegramUrl = (freeTelegramUrl ?? "").trim();
  const trimmedVipMoreHref = (vipMoreHref ?? "").trim();
  const showVipEndCta = trimmedVipMoreHref.length > 0 || Boolean(onVipCheckoutClick);
  const showPremiumInviteEndScreen =
    trimmedFreeTelegramUrl.length > 0 || showVipEndCta;

  const effectivePlaybackIds = useMemo(() => {
    if (!threeTeaserActive) return playbackIds;
    return playbackIds.slice(0, teaserCap);
  }, [playbackIds, threeTeaserActive, teaserCap]);

  /** 0-based index of the paywall reel (last teaser slot, clamped). */
  const paywallReelIndex = useMemo(() => {
    if (!threeTeaserActive || effectivePlaybackIds.length === 0) return -1;
    return Math.min(teaserCap - 1, effectivePlaybackIds.length - 1);
  }, [threeTeaserActive, effectivePlaybackIds.length, teaserCap]);

  useEffect(() => {
    if (
      !threeTeaserActive ||
      paywallReelIndex < 0 ||
      currentIndex !== paywallReelIndex
    ) {
      return;
    }
    if (seeMoreImpressionForIndex.current === currentIndex) return;
    seeMoreImpressionForIndex.current = currentIndex;
    const playbackId = effectivePlaybackIds[currentIndex];
    captureReelsAnalytics("reels_see_more_impression", {
      variant: abVariant,
      reel_index: currentIndex,
      playback_id: playbackId,
    });
    trackMuxCtaViewed({
      surface: "reels_feed_paywall",
      trigger: "paywall_panel",
      playbackId,
      limitReachedCount: teaserCap,
      maxFreeItems: teaserCap,
      videoIndex: currentIndex,
      extra: { ab_variant: abVariant },
    });
  }, [
    threeTeaserActive,
    paywallReelIndex,
    currentIndex,
    abVariant,
    effectivePlaybackIds,
  ]);

  // If the teaser list is shorter than the current index (e.g. flag switched), clamp.
  useEffect(() => {
    if (showEndScreen || isLoading) return;
    if (effectivePlaybackIds.length === 0) return;
    if (currentIndex >= effectivePlaybackIds.length) {
      setCurrentIndex(Math.max(0, effectivePlaybackIds.length - 1));
    }
  }, [effectivePlaybackIds.length, currentIndex, showEndScreen, isLoading]);

  // Update document title
  useEffect(() => {
    if (isLoading) {
      document.title = "...";
    } else if (showEndScreen) {
      document.title = "fin";
    } else if (effectivePlaybackIds.length > 0) {
      document.title = String(currentIndex + 1);
    }
  }, [currentIndex, isLoading, showEndScreen, effectivePlaybackIds.length]);

  const presetSignature = useMemo(
    () => (playbackIdsPreset?.length ? playbackIdsPreset.join("\0") : ""),
    [playbackIdsPreset],
  );

  /** Preset curado (admin) o fetch Mux + fallback */
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const preset = (playbackIdsPreset ?? []).map((x) => String(x ?? "").trim()).filter(Boolean);
      if (preset.length > 0) {
        setPlaybackIds([...new Set(preset)]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const ids = await getReelsMuxPlaybackIds();
        if (cancelled) return;
        if (ids.length > 0) {
          setPlaybackIds([...new Set(ids)]);
        } else if (fallbackPlaybackIds.length > 0) {
          setPlaybackIds([...new Set(fallbackPlaybackIds)]);
        } else {
          setPlaybackIds([]);
        }
      } catch (error) {
        console.error("Failed to fetch reels:", error);
        if (!cancelled && fallbackPlaybackIds.length > 0) {
          setPlaybackIds([...new Set(fallbackPlaybackIds)]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [presetSignature, fallbackPlaybackIds]);

  // Reset video state when changing videos
  useEffect(() => {
    setIsBuffering(true);
    setHasError(false);
    setProgress(0);
    setIsPlaying(true);
  }, [currentIndex]);

  // Check if reached end
  useEffect(() => {
    if (showEndScreen && !hasReachedEndRef.current) {
      hasReachedEndRef.current = true;
      onReachEnd?.();
    }
  }, [showEndScreen, onReachEnd]);

  // Navigate to a specific video
  const goToVideo = useCallback(
    (index: number) => {
      if (isTransitioning) return;

      // Going to end screen
      if (index >= effectivePlaybackIds.length) {
        setIsTransitioning(true);
        if (playerRef.current?.pause) playerRef.current.pause();

        setTimeout(() => {
          setShowEndScreen(true);
          setIsTransitioning(false);
        }, 200);
        return;
      }

      if (index < 0) return;

      // Coming back from end screen
      if (showEndScreen) {
        setShowEndScreen(false);
      }

      setIsTransitioning(true);

      // Pause current video
      if (playerRef.current?.pause) playerRef.current.pause();

      // Update index
      setCurrentIndex(index);
      const pid = effectivePlaybackIds[index];
      if (pid) onVideoChange?.(index, pid);

      setTimeout(() => {
        setIsTransitioning(false);
      }, 200);
    },
    [effectivePlaybackIds, isTransitioning, onVideoChange, showEndScreen]
  );

  const goToNext = useCallback(() => {
    if (showEndScreen) return;
    goToVideo(currentIndex + 1);
  }, [currentIndex, goToVideo, showEndScreen]);

  const goToPrevious = useCallback(() => {
    if (showEndScreen) {
      setShowEndScreen(false);
      goToVideo(effectivePlaybackIds.length - 1);
      return;
    }
    if (currentIndex > 0) {
      goToVideo(currentIndex - 1);
    }
  }, [currentIndex, goToVideo, showEndScreen, effectivePlaybackIds.length]);

  const restartFromBeginning = useCallback(() => {
    setShowEndScreen(false);
    hasReachedEndRef.current = false;
    goToVideo(0);
  }, [goToVideo]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart({ y: touch.clientY, time: Date.now() });
    setSwipeOffset(0);
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;
      const touch = e.touches[0];
      const deltaY = touchStart.y - touch.clientY;
      const resistance = 0.4;
      const maxOffset = 150;
      const offset =
        Math.sign(deltaY) * Math.min(Math.abs(deltaY) * resistance, maxOffset);
      setSwipeOffset(offset);
    },
    [touchStart]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;

      const touch = e.changedTouches[0];
      const deltaY = touchStart.y - touch.clientY;
      const deltaTime = Date.now() - touchStart.time;
      const velocity = Math.abs(deltaY) / deltaTime;

      setSwipeOffset(0);

      const swipeThreshold = isIOS ? 40 : 50;
      const velocityThreshold = isIOS ? 0.25 : 0.3;
      const isSwipe =
        Math.abs(deltaY) > swipeThreshold || velocity > velocityThreshold;

      if (isSwipe) {
        if (deltaY > 0) {
          goToNext();
        } else {
          goToPrevious();
        }
      }

      setTouchStart(null);
    },
    [touchStart, goToNext, goToPrevious, isIOS]
  );

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        goToNext();
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === "m") {
        setIsMuted((prev) => !prev);
      } else if (e.key === " ") {
        e.preventDefault();
        if (showEndScreen) {
          restartFromBeginning();
          return;
        }
        if (playerRef.current) {
          if (playerRef.current.paused) {
            playerRef.current.play();
            setIsPlaying(true);
          } else {
            playerRef.current.pause();
            setIsPlaying(false);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToNext, goToPrevious, showEndScreen, restartFromBeginning]);

  // Wheel navigation (desktop)
  useEffect(() => {
    if (isMobile) return;

    let lastWheelTime = 0;
    const wheelThrottle = 500;

    const handleWheel = (e: WheelEvent) => {
      const now = Date.now();
      if (now - lastWheelTime < wheelThrottle) return;

      if (Math.abs(e.deltaY) > 50) {
        lastWheelTime = now;
        if (e.deltaY > 0) {
          goToNext();
        } else {
          goToPrevious();
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: true });
      return () => container.removeEventListener("wheel", handleWheel);
    }
  }, [isMobile, goToNext, goToPrevious]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (playerRef.current) {
      if (playerRef.current.paused) {
        playerRef.current.play();
        setIsPlaying(true);
      } else {
        playerRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, []);

  const handleVideoTap = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if ("touches" in e) return;
      togglePlayPause();
    },
    [togglePlayPause]
  );

  // Keep engagement refs in sync with playlist index
  effectiveIdsRef.current = effectivePlaybackIds;

  const flushEngagement = useCallback(
    (event: "heartbeat" | "ended" | "progress", preferBeacon = false) => {
      const tok = engagementTokenRef.current;
      const ids = effectiveIdsRef.current;
      const idx = currentIndexRef.current;
      const pid = ids[idx];
      if (!tok || !pid) return;
      const now = Date.now();
      const elapsedMs = now - lastBeaconAtRef.current;
      const pos = lastPositionRef.current;
      let delta = elapsedMs / 1000;
      if (delta < 0 || delta > 30) delta = 0;
      delta = Math.min(Math.max(delta, 0), 30);
      if (event === "heartbeat" && delta < 0.5 && !preferBeacon) return;
      lastBeaconAtRef.current = now;
      sendSecretShareBeacon(
        {
          token: tok,
          event,
          playback_id: pid,
          position_seconds: pos,
          duration_seconds: lastDurationRef.current || undefined,
          delta_watch_seconds: delta,
        },
        { preferBeacon },
      );
    },
    [],
  );

  // Flush remaining watch time when leaving the page / tab
  useEffect(() => {
    if (!engagementToken) return;
    const onHide = () => flushEngagement("heartbeat", true);
    const onVis = () => {
      if (document.visibilityState === "hidden") onHide();
    };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVis);
      flushEngagement("heartbeat", true);
    };
  }, [engagementToken, flushEngagement]);

  // Reset position tracker when reel changes (do not zero mid-resume)
  useEffect(() => {
    lastBeaconAtRef.current = Date.now();
    lastPositionRef.current = 0;
    lastDurationRef.current = 0;
    playSentForIndexRef.current = null;
  }, [currentIndex]);

  // Video event handlers - simple and direct
  const handleCanPlay = useCallback(() => {
    setIsBuffering(false);
    // Auto-play when ready
    if (playerRef.current && playerRef.current.paused) {
      playerRef.current.play().catch(() => {});
    }
  }, []);

  // When video ends, go to next (except paywall reel in three-teaser — stay for CTA)
  const handleEnded = useCallback(() => {
    flushEngagement("ended");
    if (
      threeTeaserActive &&
      paywallReelIndex >= 0 &&
      currentIndex === paywallReelIndex
    ) {
      return;
    }
    goToNext();
  }, [
    goToNext,
    threeTeaserActive,
    paywallReelIndex,
    currentIndex,
    flushEngagement,
  ]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setIsBuffering(false);
    const pid = effectivePlaybackIds[currentIndex];
    onVideoPlay?.(currentIndex, pid);
    const tok = engagementTokenRef.current;
    // Only one "play" per reel index (resume should not re-count / zero position)
    if (tok && pid && playSentForIndexRef.current !== currentIndex) {
      playSentForIndexRef.current = currentIndex;
      lastBeaconAtRef.current = Date.now();
      sendSecretShareBeacon({
        token: tok,
        event: "play",
        playback_id: pid,
        position_seconds: lastPositionRef.current || 0,
      });
    }
  }, [currentIndex, onVideoPlay, effectivePlaybackIds]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    flushEngagement("heartbeat");
  }, [flushEngagement]);

  const handleWaiting = useCallback(() => {
    setIsBuffering(true);
  }, []);

  const handlePlaying = useCallback(() => {
    setIsBuffering(false);
  }, []);

  const handleError = useCallback(
    (e?: unknown) => {
      setIsBuffering(false);
      setHasError(true);
      const tok = engagementTokenRef.current;
      if (!tok) return;
      const pid = effectiveIdsRef.current[currentIndexRef.current] || "";
      const detail =
        e && typeof e === "object" && "detail" in e
          ? (e as { detail?: unknown }).detail
          : e;
      let msg = "mux_player_error";
      let code: string | undefined;
      if (detail && typeof detail === "object") {
        const d = detail as Record<string, unknown>;
        msg = String(d.message || d.error || d.type || msg).slice(0, 500);
        if (d.code != null) code = String(d.code).slice(0, 64);
      } else if (typeof detail === "string") {
        msg = detail.slice(0, 500);
      }
      sendSecretShareBeacon({
        token: tok,
        event: "error",
        playback_id: pid || undefined,
        error_message: msg,
        error_code: code,
        position_seconds: lastPositionRef.current || undefined,
      });
    },
    [],
  );

  const handleTimeUpdate = useCallback(
    (e: any) => {
      const video = e.target;
      if (!video || !video.duration) return;

      const pos = Number(video.currentTime) || 0;
      const dur = Number(video.duration) || 0;
      const prevPos = lastPositionRef.current;
      const prog = dur > 0 ? (pos / dur) * 100 : 0;
      setProgress(prog);
      lastPositionRef.current = pos;
      lastDurationRef.current = dur;

      const tok = engagementTokenRef.current;
      const pid = effectivePlaybackIds[currentIndex];
      if (!tok || !pid) return;

      const now = Date.now();
      const elapsedMs = now - lastBeaconAtRef.current;
      if (elapsedMs < 5000) return;

      // Prefer wall-clock while playing; media delta only as sanity check
      let delta = elapsedMs / 1000;
      const mediaDelta = pos - prevPos;
      if (mediaDelta > 0 && mediaDelta <= 30) {
        delta = Math.min(delta, mediaDelta + 0.5);
      }
      delta = Math.min(Math.max(delta, 0), 30);

      lastBeaconAtRef.current = now;
      sendSecretShareBeacon({
        token: tok,
        event: "heartbeat",
        playback_id: pid,
        position_seconds: pos,
        duration_seconds: dur || undefined,
        delta_watch_seconds: delta,
      });
    },
    [currentIndex, effectivePlaybackIds],
  );

  const retryVideo = useCallback(() => {
    setHasError(false);
    setIsBuffering(true);
    // Force remount by briefly clearing and restoring
    const idx = currentIndex;
    setCurrentIndex(-1);
    setTimeout(() => setCurrentIndex(idx), 50);
  }, [currentIndex]);

  // Styles
  const videoStyles = useMemo(
    () => `
    .reels-feed-container {
      width: 100%;
      height: 100dvh;
      height: 100vh;
      overflow: hidden;
      position: fixed;
      inset: 0;
      background: #000;
      touch-action: pan-y;
      -webkit-overflow-scrolling: touch;
    }

    .reels-video-wrapper {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000;
    }

    .reels-mux-player {
      width: 100% !important;
      height: 100% !important;
      max-width: 100vw !important;
      max-height: 100dvh !important;
      max-height: 100vh !important;
      --controls: none !important;
      --media-object-fit: cover !important;
    }

    .reels-mux-player video {
      object-fit: cover !important;
      width: 100% !important;
      height: 100% !important;
    }

    @media (min-width: 768px) {
      .reels-mux-player {
        max-width: min(56.25vh, 100vw) !important;
        --media-object-fit: contain !important;
      }
      .reels-mux-player video {
        object-fit: contain !important;
      }
    }

    @supports (-webkit-touch-callout: none) {
      .reels-feed-container {
        height: -webkit-fill-available;
      }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-fade-in-up {
      animation: fadeInUp 0.5s ease-out forwards;
    }

    @media (prefers-reduced-motion: reduce) {
      .animate-fade-in-up {
        animation: none;
        opacity: 1;
        transform: none;
      }
    }

    .progress-bar {
      transition: width 0.1s linear;
    }

    @keyframes pulse-ring {
      0% { transform: scale(0.8); opacity: 1; }
      100% { transform: scale(1.3); opacity: 0; }
    }

    .buffering-ring {
      animation: pulse-ring 1s ease-out infinite;
    }
  `,
    []
  );

  // Current playback ID
  const currentPlaybackId = effectivePlaybackIds[currentIndex];

  // Loading screen
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-2 border-white/20" />
            <div className="absolute inset-0 rounded-full border-2 border-white border-t-transparent animate-spin" />
          </div>
          <p className="text-white/60 text-sm">{rt("loading")}</p>
        </div>
      </div>
    );
  }

  // No videos
  if (effectivePlaybackIds.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center px-8">
          <p className="text-white/60 text-sm mb-4">
            {rt("noVideos")}
          </p>
          <a
            href="/"
            className="text-white/40 hover:text-white/60 text-xs transition-colors"
          >
            {rt("back")}
          </a>
        </div>
      </div>
    );
  }

  // End screen
  if (showEndScreen) {
    const endHeadline =
      endScreenCtaLabels?.headline?.trim() || rt("seeMoreContent");
    const endFreeLabel =
      endScreenCtaLabels?.freeTelegram?.trim() || rt("freeDefault");
    const endVipLabel =
      endScreenCtaLabels?.vip?.trim() || rt("vipDefault");

    if (showPremiumInviteEndScreen) {
      return (
        <>
          <style>{videoStyles}</style>
          <div
            ref={containerRef}
            className="fixed inset-0 flex flex-col justify-end overflow-hidden bg-black px-4"
            style={{ paddingBottom: 'max(1.5rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))', paddingTop: 'max(2rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Solid panel only — no backdrop-blur over content */}
            <div
              className="relative z-10 mx-auto w-full max-w-md animate-fade-in-up rounded-2xl border border-white/20 bg-zinc-950 p-5 shadow-[0_16px_56px_rgba(0,0,0,0.65)]"
              style={{ backdropFilter: "none" }}
            >
              <p className="mb-4 text-center text-base font-medium leading-snug text-white">
                {endHeadline}
              </p>
              <div className="flex flex-col gap-3">
                {trimmedFreeTelegramUrl ? (
                  <a
                    href={trimmedFreeTelegramUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 py-3.5 text-center text-sm font-semibold text-white transition-colors hover:bg-white/15"
                    onClick={() =>
                      captureReelsAnalytics("reels_end_free_telegram_click", {
                        href: trimmedFreeTelegramUrl,
                      })
                    }
                  >
                    <ExternalLink className="h-4 w-4 opacity-90" />
                    {endFreeLabel}
                  </a>
                ) : null}
                {showVipEndCta ? (
                  onVipCheckoutClick && !trimmedVipMoreHref ? (
                    <button
                      type="button"
                      onClick={() => {
                        captureReelsAnalytics("reels_end_vip_click", {
                          gateway: "stripe_subscription_vip",
                          flow: "premium_invite",
                        });
                        onVipCheckoutClick();
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 py-3.5 text-center text-sm font-semibold text-white shadow-lg transition-opacity hover:opacity-95"
                    >
                      {endVipLabel}
                    </button>
                  ) : (
                    <a
                      href={trimmedVipMoreHref}
                      className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 py-3.5 text-center text-sm font-semibold text-white shadow-lg transition-opacity hover:opacity-95"
                      onClick={() => {
                        captureReelsAnalytics("reels_end_vip_click", {
                          href: trimmedVipMoreHref,
                          flow: "premium_invite",
                          funnel: "comunidad_ar",
                          product_surface: "comunidad",
                        });
                        trackComunidadReelsVipClickPostHog({
                          country:
                            (
                              window as unknown as {
                                __TEMPLATE_CONFIG__?: { country?: string };
                              }
                            ).__TEMPLATE_CONFIG__?.country || "UNKNOWN",
                          vip_checkout_gateway: /mercadopago/i.test(
                            trimmedVipMoreHref,
                          )
                            ? "mercadopago"
                            : "other",
                          has_vip_checkout_override: true,
                          cta_destination: trimmedVipMoreHref,
                        });
                      }}
                    >
                      {endVipLabel}
                    </a>
                  )
                ) : null}
                {/* AR / GeoSim AR: logo MP bajo CTA VIP de fin de reels */}
                {showVipEndCta &&
                  (isArgentinaPaymentContext(null, null) ||
                    /mercadopago/i.test(trimmedVipMoreHref || "")) && (
                    <div
                      className="mt-1 flex justify-center rounded-lg bg-white px-3 py-1.5"
                      aria-label="Pago seguro con Mercado Pago"
                    >
                      <img
                        src={BRAND_MERCADOPAGO_HANDSHAKE_URL}
                        alt="Mercado Pago — pago seguro"
                        className="h-6 w-auto max-w-[min(100%,180px)] object-contain"
                        loading="lazy"
                        draggable={false}
                      />
                    </div>
                  )}
              </div>
            </div>

            <div className="relative z-10 mx-auto mt-5 flex justify-center">
              <button
                type="button"
                onClick={restartFromBeginning}
                className="flex items-center gap-2 text-white/40 transition-colors hover:text-white/60 text-xs"
              >
                <RefreshCw className="h-3 w-3" />
                <span>{rt("watchAgain")}</span>
              </button>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <style>{videoStyles}</style>
        <div
          ref={containerRef}
          className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="relative z-10 text-center px-8 max-w-md">
            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "0ms" }}
            >
              <h1 className="text-xl md:text-2xl font-light text-white mb-6 leading-relaxed">
                Hay días y noches que cuestan más,
                <br />
                <span className="font-medium">
                  pero ahí se nota la diferencia de quienes se rinden y quienes
                  siguen adelante
                </span>
              </h1>
            </div>

            <div
              className="animate-fade-in-up"
              style={{ animationDelay: "300ms" }}
            >
              <Heart className="w-5 h-5 text-white/30 mx-auto" />
            </div>
          </div>

          <div
            className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in-up"
            style={{ animationDelay: "600ms" }}
          >
            <button
              onClick={restartFromBeginning}
              className="flex items-center gap-2 text-white/30 hover:text-white/50 text-xs transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              <span>{rt("watchAgain")}</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{videoStyles}</style>

      <div
        ref={containerRef}
        className="reels-feed-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform:
            swipeOffset !== 0 ? `translateY(${-swipeOffset}px)` : undefined,
          transition: swipeOffset === 0 ? "transform 0.2s ease-out" : "none",
        }}
      >
        {/* Single video player */}
        {currentPlaybackId && currentIndex >= 0 && (
          <div className="reels-video-wrapper" onClick={handleVideoTap}>
            {/* Buffering indicator */}
            {isBuffering && !hasError && (
              <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border-2 border-white/30 buffering-ring" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full border-2 border-white/50 border-t-transparent animate-spin" />
                  </div>
                </div>
              </div>
            )}

            {/* Error overlay */}
            {hasError && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/90">
                <div className="text-center px-8">
                  <p className="text-white/70 text-sm mb-4">
                    {rt("loadError")}
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={retryVideo}
                      className="px-5 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full text-sm transition-colors"
                    >
                      {rt("retry")}
                    </button>
                    {currentIndex < effectivePlaybackIds.length - 1 && (
                      <button
                        onClick={goToNext}
                        className="text-white/40 hover:text-white/60 text-xs"
                      >
                        {rt("next")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Pause overlay */}
            {!isPlaying && !isBuffering && !hasError && (
              <div className="absolute inset-0 z-15 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-8 h-8 text-white ml-1" fill="white" />
                </div>
              </div>
            )}

            <MuxPlayer
              key={`reel-${currentIndex}`}
              ref={playerRef}
              streamType="on-demand"
              playbackId={currentPlaybackId}
              autoPlay
              muted={isMuted}
              playsInline
              preload="auto"
              // Disable unnecessary features to reduce network requests
              disableCookies
              storyboardSrc=""
              thumbnailTime={0}
              startTime={0}
              // disableCookies only turns off Mux Data's cross-page cookie —
              // envKey + metadata still send litix beacons (perfect for secret shares).
              envKey={muxDataEnvKey?.trim() || MUX_DATA_ENV_KEY}
              metadata={{
                video_id: currentPlaybackId,
                video_title: engagementToken
                  ? `secret-share:${String(engagementToken).slice(0, 8)}`
                  : `reels:${currentIndex + 1}`,
                video_series: engagementToken ? "secret_share" : "reels_feed",
                video_stream_type: "on-demand",
                // Mux Data filters: custom_1=token, custom_2=secret_share, viewer_user_id=token
                ...(engagementToken
                  ? {
                      viewer_user_id: String(engagementToken),
                      experiment_name: "secret_share",
                      custom_1: String(engagementToken),
                      custom_2: "secret_share",
                      custom_3: String(currentIndex + 1),
                      player_name: "delfuocampo-secret-share",
                    }
                  : {
                      player_name: "delfuocampo-reels-feed",
                    }),
              }}
              onCanPlay={handleCanPlay}
              onPlay={handlePlay}
              onPause={handlePause}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
              onEnded={handleEnded}
              onError={handleError}
              onTimeUpdate={handleTimeUpdate}
              className="reels-mux-player"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        )}

        {/* UI overlay */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/10">
            <div
              className="h-full bg-white/80 progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Top controls */}
          <div
            className="absolute left-0 right-0 px-4 flex items-center justify-between"
            style={{ top: 'max(0.75rem, calc(env(safe-area-inset-top, 0px) + 0.5rem))' }}
          >
            {/* Video counter */}
            <div className="px-2.5 py-1 bg-black/30 backdrop-blur-sm rounded-full text-white/80 text-xs font-medium">
              {currentIndex + 1}/{effectivePlaybackIds.length}
            </div>

            {/* Mute button */}
            <button
              onClick={toggleMute}
              className="w-11 h-11 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white/80 pointer-events-auto active:scale-95 transition-transform"
              aria-label={isMuted ? rt("muteOn") : rt("muteOff")}
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Navigation arrows (desktop only) */}
          {!isMobile && (
            <>
              {currentIndex > 0 && (
                <button
                  onClick={goToPrevious}
                  className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 -mt-20 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/60 hover:text-white pointer-events-auto ui-transition"
                  aria-label="Anterior"
                >
                  <ChevronUp className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={goToNext}
                className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 mt-20 w-10 h-10 bg-black/20 hover:bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white/60 hover:text-white pointer-events-auto ui-transition"
                aria-label="Siguiente"
              >
                <ChevronDown className="w-5 h-5" />
              </button>
            </>
          )}

          {/* Swipe hint (first video on mobile) */}
          {isMobile && currentIndex === 0 && effectivePlaybackIds.length > 1 && (
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 animate-bounce pointer-events-none">
              <ChevronUp className="w-6 h-6 text-white/40" />
            </div>
          )}

          {/* Three-teaser paywall: solid panel, no backdrop-blur on video */}
          {threeTeaserActive &&
            paywallReelIndex >= 0 &&
            currentIndex === paywallReelIndex && (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center px-4 pt-20"
                style={{ paddingBottom: 'max(2rem, calc(env(safe-area-inset-bottom, 0px) + 2rem))' }}
              >
                <div
                  className="pointer-events-auto w-full max-w-md rounded-2xl border border-white/25 bg-zinc-950/95 p-4 shadow-[0_12px_48px_rgba(0,0,0,0.55)]"
                  style={{ backdropFilter: "none" }}
                >
                  <p className="mb-3 text-center text-sm font-medium leading-snug text-white">
                    {rt("seeMoreContent")}
                  </p>
                  <a
                    href={seeMoreHref}
                    className="flex w-full items-center justify-center rounded-xl bg-white py-3 text-center text-sm font-semibold text-black transition-colors hover:bg-white/90"
                    onClick={() => {
                      captureReelsAnalytics("reels_see_more_click", {
                        variant: abVariant,
                        reel_index: currentIndex,
                        href: seeMoreHref,
                      });
                      trackMuxCheckoutClick({
                        surface: "reels_feed_paywall",
                        trigger: "paywall_panel",
                        playbackId: effectivePlaybackIds[currentIndex],
                        limitReachedCount: teaserCap,
                        maxFreeItems: teaserCap,
                        videoIndex: currentIndex,
                        gateway: "external_url",
                        extra: {
                          ab_variant: abVariant,
                          see_more_href: seeMoreHref,
                        },
                      });
                    }}
                  >
                    {rt("seeMore")}
                  </a>
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default ReelsFeed;
